import type { ReactNode } from 'react';

/** A titled card. Every block on the profile page is one of these. */
export function ProfileSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-soft">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-ink">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/**
 * A label/value pair. `value` falls back to a muted placeholder so an empty
 * profile reads as "not filled in yet" rather than as a broken layout.
 */
export function InfoRow({
  label,
  value,
  placeholder = 'Not added yet',
}: {
  label: string;
  value?: ReactNode;
  placeholder?: string;
}) {
  const empty = value === null || value === undefined || value === '';

  return (
    <div className="flex flex-col gap-0.5 border-b border-line py-3 last:border-0 last:pb-0 first:pt-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-44 shrink-0 text-sm font-semibold text-slate-500">{label}</dt>
      <dd className={`text-sm ${empty ? 'text-slate-400 italic' : 'text-ink'}`}>
        {empty ? placeholder : value}
      </dd>
    </div>
  );
}

/** An empty-state line for a section the account has not filled in. */
export function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-dashed border-line bg-canvas px-4 py-5 text-center text-sm text-slate-500">
      {children}
    </p>
  );
}
